import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ShieldCheck, Plus, X, Trash2, ChevronRight, Camera, AlertTriangle, Printer, Upload } from 'lucide-react';
import QuickAddCustomerButton from '../components/QuickAddCustomerDialog';
import ExternalRmaDialog from '../components/warranty/ExternalRmaDialog';
import RmaAttachments from '../components/warranty/RmaAttachments';
import LinkedPosCard from '../components/purchasing/LinkedPosCard';
import { toast } from 'sonner';
import { openBrandedPrintWindow, PrintHelpers as H } from '../utils/brandedPrint';

const TYPES = [
  { id: 'customer_rma', label: 'Customer RMA', desc: 'Returned by customer for triage' },
  { id: 'vendor_claim', label: 'Vendor Claim', desc: 'Defect returned to supplier' },
];
const STATUS_COLOR = {
  open: 'bg-blue-100 text-blue-700',
  in_review: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  completed: 'bg-slate-100 text-slate-700',
};

const WarrantyPage = () => {
  const { api } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [extOpen, setExtOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [transitionTo, setTransitionTo] = useState(null);
  const [resolution, setResolution] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [tR, vR, iR, uR, cR] = await Promise.all([
        api().get('/warranty-tickets'),
        api().get('/vendors').catch(() => ({ data: [] })),
        api().get('/items').catch(() => ({ data: [] })),
        api().get('/finished-units').catch(() => ({ data: [] })),
        api().get('/customers').catch(() => ({ data: [] })),
      ]);
      setTickets(tR.data || []);
      setVendors((vR.data || []).filter(v => !v.archived));
      setItems(iR.data || []);
      setUnits(uR.data || []);
      setCustomers(cR.data || []);
    } catch (e) { toast.error('Failed to load warranty tickets'); }
    finally { setLoading(false); }
  };

  // iter67 — branded printable warranty certificate
  const printWarranty = async (t) => {
    if (!t) return;
    const isCust = t.ticket_type === 'customer_rma';
    const left = isCust
      ? `${H.kv('Customer', `${H.escape(t.customer_name || '—')}<br/><span class="mono" style="font-size:9pt; color:#666;">${H.escape(t.customer_email || '')}</span>`)}
         ${H.kv('Serial Number', H.escape(t.serial_number || '—'), true)}
         ${H.kv('Item', `${H.escape(t.item_sku || '')} · ${H.escape(t.item_name || '')}`, true)}`
      : `${H.kv('Vendor', H.escape(t.vendor_name || '—'))}
         ${H.kv('Item', `${H.escape(t.item_sku || '')} · ${H.escape(t.item_name || '')}`, true)}
         ${t.batch_lot ? H.kv('Batch / Lot', `${H.escape(t.batch_lot)} · qty ${t.quantity}`, true) : ''}`;
    const right = `
      ${H.kv('Status', H.escape((t.status || '').replace('_', ' ').toUpperCase()))}
      ${H.kv('Opened', `${H.escape(t.opened_by_name || '')}<br/><span style="font-size:9pt; color:#888;">${t.opened_at ? new Date(t.opened_at).toLocaleString() : ''}</span>`)}
      ${t.resolved_at ? H.kv('Resolved', `${H.escape(t.resolved_by_name || '')}<br/><span style="font-size:9pt; color:#888;">${new Date(t.resolved_at).toLocaleString()}</span>`) : ''}
      ${t.claim_amount > 0 ? H.kv('Claim Amount', `<strong>$${Number(t.claim_amount).toFixed(2)}</strong>`, true) : ''}
      ${t.photo_data ? `<div class="label">Evidence</div><img src="${t.photo_data}" style="max-width:100%; max-height:240px; border:1px solid #e5e7eb; border-radius:4px;" alt="evidence" />` : ''}`;
    const body = `
      <div class="grid-2">
        <div>${left}</div>
        <div>${right}</div>
      </div>
      ${H.sectionTitle('Description')}
      <div class="value" style="white-space: pre-wrap;">${H.escape(t.description || '—')}</div>
      ${t.notes ? `${H.sectionTitle('Notes')}<div class="value" style="white-space: pre-wrap;">${H.escape(t.notes)}</div>` : ''}
      ${t.resolution ? `${H.sectionTitle('Resolution')}<div class="callout" style="white-space: pre-wrap;">${H.escape(t.resolution)}</div>` : ''}
      ${H.signoffLines('Authorized signature', 'Date')}`;
    const statusColor = ({
      open: '#2563eb', in_review: '#d97706', approved: '#059669',
      rejected: '#dc2626', completed: '#475569',
    })[t.status] || '#475569';
    await openBrandedPrintWindow({
      title: isCust ? 'WARRANTY CLAIM' : 'VENDOR WARRANTY CLAIM',
      subtitle: t.ticket_number,
      statusBadge: H.statusBadge(t.status?.replace('_', ' '), statusColor),
      body,
      footerNote: 'Internal Document · Warranty & Returns',
    });
  };


  useEffect(() => { fetchAll(); }, []);

  const filtered = tab === 'all' ? tickets : tab.startsWith('type:') ? tickets.filter(t => t.ticket_type === tab.slice(5)) : tickets.filter(t => t.status === tab);

  const pickCustomer = (c) => setForm(f => ({ ...f, customer_id: c?.id || '', customer_name: c?.customer_name || f.customer_name, customer_email: c?.email || f.customer_email, customer_phone: c?.phone || f.customer_phone }));

  const startNew = (ticket_type = 'customer_rma') => {
    setCreating(true);
    setForm({
      ticket_type, customer_name: '', customer_email: '', customer_phone: '',
      finished_unit_id: null, serial_number: '',
      vendor_id: null, item_id: null, item_sku: '', item_name: '', batch_lot: '', quantity: 1,
      description: '', photo_data: null, claim_amount: 0, notes: '',
    });
  };

  const onPhoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('Photo must be < 5MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(prev => ({ ...prev, photo_data: reader.result }));
    reader.readAsDataURL(f);
  };

  const setItemFromCatalog = (id) => {
    if (!id) { setForm(f => ({ ...f, item_id: null, item_sku: '', item_name: '' })); return; }
    const it = items.find(i => i.id === id);
    if (!it) return;
    setForm(f => ({ ...f, item_id: it.id, item_sku: it.item_sku, item_name: it.item_name }));
  };

  const setUnitFromCatalog = (id) => {
    if (!id) { setForm(f => ({ ...f, finished_unit_id: null, serial_number: '' })); return; }
    const u = units.find(x => x.id === id);
    if (!u) return;
    setForm(f => ({ ...f, finished_unit_id: u.id, serial_number: u.serial_number }));
  };

  const save = async () => {
    if (!form.description?.trim()) { toast.error('Description required'); return; }
    if (form.ticket_type === 'customer_rma' && !form.serial_number && !form.finished_unit_id) { toast.error('Serial or unit required'); return; }
    if (form.ticket_type === 'vendor_claim' && (!form.vendor_id || (!form.item_id && !form.item_sku))) { toast.error('Vendor + item required'); return; }
    try {
      await api().post('/warranty-tickets', form);
      toast.success('Warranty ticket created');
      setCreating(false);
      setForm(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const transition = async () => {
    try {
      await api().post(`/warranty-tickets/${viewing.id}/transition`, { to_status: transitionTo, resolution });
      toast.success(`Status: ${transitionTo}`);
      const r = await api().get(`/warranty-tickets/${viewing.id}`);
      setViewing(r.data);
      setTransitionTo(null);
      setResolution('');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Transition failed'); }
  };

  const deleteTicket = async (t) => {
    if (!window.confirm(`Delete warranty ticket ${t.ticket_number}?`)) return;
    try {
      await api().delete(`/warranty-tickets/${t.id}`);
      toast.success('Deleted');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  const counts = {
    open: tickets.filter(t => t.status === 'open').length,
    in_review: tickets.filter(t => t.status === 'in_review').length,
    approved: tickets.filter(t => t.status === 'approved').length,
    completed: tickets.filter(t => t.status === 'completed').length,
    rma: tickets.filter(t => t.ticket_type === 'customer_rma').length,
    claim: tickets.filter(t => t.ticket_type === 'vendor_claim').length,
  };

  const validTransitions = {
    open: ['in_review', 'rejected'],
    in_review: ['approved', 'rejected'],
    approved: ['completed'],
  };

  return (
    <div className="space-y-6 animate-slideIn" data-testid="warranty-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Warranty</h1>
          <p className="text-slate-500 text-sm">Customer RMA and Vendor Claim ticket management with audit trail.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setExtOpen(true)} className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="warranty-upload-external-btn"><Upload className="w-4 h-4 mr-1" /> Upload External RMA</Button>
          <Button variant="outline" onClick={() => startNew('vendor_claim')} data-testid="warranty-new-claim-btn"><Plus className="w-4 h-4 mr-1" /> Vendor Claim</Button>
          <Button onClick={() => startNew('customer_rma')} className="bg-slate-900 hover:bg-slate-800" data-testid="warranty-new-rma-btn"><Plus className="w-4 h-4 mr-1" /> Customer RMA</Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all">All ({tickets.length})</TabsTrigger>
          <TabsTrigger value="open">Open ({counts.open})</TabsTrigger>
          <TabsTrigger value="in_review">In Review ({counts.in_review})</TabsTrigger>
          <TabsTrigger value="approved">Approved ({counts.approved})</TabsTrigger>
          <TabsTrigger value="completed">Completed ({counts.completed})</TabsTrigger>
          <TabsTrigger value="type:customer_rma">RMA ({counts.rma})</TabsTrigger>
          <TabsTrigger value="type:vendor_claim">Vendor Claims ({counts.claim})</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          <Card className="industrial-card">
            <CardContent className="p-0">
              {filtered.length === 0 ? (
                <div className="p-12 text-center text-slate-500"><ShieldCheck className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="font-medium">No tickets</p></div>
              ) : (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ticket #</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Claim $</TableHead>
                      <TableHead>Opened</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map(t => (
                      <TableRow key={t.id} data-testid={`warranty-row-${t.id}`}>
                        <TableCell><button onClick={() => setViewing(t)} className="mono text-orange-600 hover:underline">{t.ticket_number}</button></TableCell>
                        <TableCell className="text-xs">{TYPES.find(x => x.id === t.ticket_type)?.label}{t.source === 'external' && <span className="ml-1 text-[10px] uppercase bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded-sm" data-testid={`warranty-external-badge-${t.id}`}>External</span>}</TableCell>
                        <TableCell className="text-sm">
                          {t.ticket_type === 'customer_rma' ? <><span className="mono">{t.serial_number}</span> · {t.customer_name || ''}</> : <><span>{t.vendor_name || '—'}</span> · <span className="mono">{t.item_sku}</span></>}
                          <div className="text-xs text-slate-500 truncate max-w-[300px]">{t.description}</div>
                        </TableCell>
                        <TableCell><span className={`text-xs px-2 py-0.5 rounded-sm ${STATUS_COLOR[t.status]}`}>{t.status?.replace('_', ' ')}</span></TableCell>
                        <TableCell className="text-right mono text-sm">{t.claim_amount ? `$${Number(t.claim_amount).toFixed(2)}` : '—'}</TableCell>
                        <TableCell className="text-xs text-slate-500">{new Date(t.opened_at).toLocaleDateString()}<br /><span className="text-[10px]">{t.opened_by_name}</span></TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => setViewing(t)} data-testid={`warranty-view-${t.id}`}><ChevronRight className="w-4 h-4" /></Button>
                          {t.status === 'open' && <Button size="sm" variant="ghost" onClick={() => deleteTicket(t)} className="text-red-600" data-testid={`warranty-delete-${t.id}`}><Trash2 className="w-4 h-4" /></Button>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Dialog */}
      {creating && form && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setCreating(false)}>
          <div className="bg-white rounded-sm max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="warranty-dialog">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <div>
                <h2 className="text-lg font-semibold">New Warranty Ticket — {TYPES.find(t => t.id === form.ticket_type)?.label}</h2>
                <p className="text-xs text-slate-500">{TYPES.find(t => t.id === form.ticket_type)?.desc}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setCreating(false)}><X className="w-4 h-4" /></Button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <Label>Type</Label>
                <select value={form.ticket_type} onChange={(e) => setForm({ ...form, ticket_type: e.target.value })} className="w-full border border-slate-200 rounded-sm h-9 px-3 text-sm" data-testid="warranty-form-type">
                  {TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>

              {form.ticket_type === 'customer_rma' ? (
                <>
                  <div>
                    <Label>Customer (from customer list)</Label>
                    <div className="flex gap-2">
                      <select value={form.customer_id || ''} onChange={(e) => pickCustomer(customers.find(c => c.id === e.target.value))} className="flex-1 border border-slate-200 rounded-sm h-9 px-3 text-sm" data-testid="warranty-form-customer">
                        <option value="">— pick a customer or type below —</option>
                        {customers.map(c => <option key={c.id} value={c.id}>{c.customer_name}</option>)}
                      </select>
                      <QuickAddCustomerButton api={api} testId="warranty-new-customer" onCreated={(c) => { setCustomers(prev => [...prev, c].sort((x, y) => (x.customer_name || '').localeCompare(y.customer_name || ''))); pickCustomer(c); }} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Customer Name</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="rounded-sm" /></div>
                    <div><Label>Customer Email</Label><Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="rounded-sm mono" /></div>
                  </div>
                  <div><Label>Customer Phone</Label><Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="rounded-sm mono" /></div>
                  <div>
                    <Label>Finished Unit (by serial)</Label>
                    <select value={form.finished_unit_id || ''} onChange={(e) => setUnitFromCatalog(e.target.value || null)} className="w-full border border-slate-200 rounded-sm h-9 px-3 text-sm" data-testid="warranty-form-unit">
                      <option value="">— select unit —</option>
                      {units.map(u => <option key={u.id} value={u.id}>{u.serial_number}</option>)}
                    </select>
                    {!form.finished_unit_id && (
                      <Input placeholder="…or paste serial #" value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} className="rounded-sm mono mt-1" data-testid="warranty-form-serial" />
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label>Vendor *</Label>
                    <select value={form.vendor_id || ''} onChange={(e) => setForm({ ...form, vendor_id: e.target.value || null })} className="w-full border border-slate-200 rounded-sm h-9 px-3 text-sm" data-testid="warranty-form-vendor">
                      <option value="">— select vendor —</option>
                      {vendors.map(v => <option key={v.id} value={v.id}>{v.vendor_name}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Item *</Label>
                    <select value={form.item_id || ''} onChange={(e) => setItemFromCatalog(e.target.value || null)} className="w-full border border-slate-200 rounded-sm h-9 px-3 text-sm" data-testid="warranty-form-item">
                      <option value="">— select item —</option>
                      {items.map(it => <option key={it.id} value={it.id}>{it.item_sku} · {it.item_name}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Batch / Lot</Label><Input value={form.batch_lot} onChange={(e) => setForm({ ...form, batch_lot: e.target.value })} className="rounded-sm mono" /></div>
                    <div><Label>Quantity</Label><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: parseFloat(e.target.value) || 1 })} className="rounded-sm mono" /></div>
                  </div>
                </>
              )}

              <div>
                <Label>Description *</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="rounded-sm" placeholder="What's the issue? Symptoms, history, expected resolution..." data-testid="warranty-form-description" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Claim Amount ($)</Label><Input type="number" step="0.01" value={form.claim_amount} onChange={(e) => setForm({ ...form, claim_amount: parseFloat(e.target.value) || 0 })} className="rounded-sm mono" /></div>
                <div><Label className="flex items-center gap-1"><Camera className="w-3 h-3" /> Photo</Label><input type="file" accept="image/*" onChange={onPhoto} className="text-xs mt-1" /></div>
              </div>
              {form.photo_data && <img src={form.photo_data} alt="evidence" className="max-h-40 border border-slate-200 rounded-sm" />}
              <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="rounded-sm" /></div>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
              <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
              <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="warranty-save-btn">Create</Button>
            </div>
          </div>
        </div>
      )}

      <ExternalRmaDialog api={api} open={extOpen} onOpenChange={setExtOpen} customers={customers} setCustomers={setCustomers} onSaved={(t) => { fetchAll(); setViewing(t); }} />

      {/* View dialog */}
      {viewing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setViewing(null)}>
          <div className="bg-white rounded-sm max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="warranty-view-dialog">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">{viewing.ticket_number}</h2>
                <span className={`text-xs px-2 py-0.5 rounded-sm ${STATUS_COLOR[viewing.status]}`}>{viewing.status?.replace('_', ' ')}</span>
                <span className="text-xs text-slate-400 ml-2">{TYPES.find(t => t.id === viewing.ticket_type)?.label}</span>
                {viewing.source === 'external' && <span className="ml-2 text-[10px] uppercase bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded-sm" data-testid="warranty-view-external-badge">External</span>}
              </div>
              <Button variant="ghost" size="sm" onClick={() => setViewing(null)}><X className="w-4 h-4" /></Button>
            </div>
            <div className="p-5 space-y-3">
              {viewing.ticket_type === 'customer_rma' ? (
                <>
                  <div><span className="text-xs uppercase text-slate-500 block">Customer</span><span className="text-sm">{viewing.customer_name || '—'} · <span className="mono">{viewing.customer_email || ''}</span></span></div>
                  <div><span className="text-xs uppercase text-slate-500 block">Serial #</span><span className="text-sm mono">{viewing.serial_number || '—'}</span></div>
                  {viewing.source === 'external' && (
                    <div className="grid grid-cols-2 gap-3 text-sm" data-testid="warranty-view-external-info">
                      <div><span className="text-xs uppercase text-slate-500 block">External RMA #</span><span className="mono">{viewing.external_rma_number || '—'}</span></div>
                      <div><span className="text-xs uppercase text-slate-500 block">Product</span>{viewing.item_name || '—'}</div>
                      <div><span className="text-xs uppercase text-slate-500 block">RMA Date</span>{viewing.rma_date || '—'}</div>
                      <div><span className="text-xs uppercase text-slate-500 block">Completed Date</span>{viewing.completed_date || '—'}</div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div><span className="text-xs uppercase text-slate-500 block">Vendor</span><span className="text-sm">{viewing.vendor_name || '—'}</span></div>
                  <div><span className="text-xs uppercase text-slate-500 block">Item</span><span className="text-sm mono">{viewing.item_sku}</span> {viewing.item_name && <span className="text-xs text-slate-500">· {viewing.item_name}</span>}</div>
                  {viewing.batch_lot && <div><span className="text-xs uppercase text-slate-500 block">Batch / Lot</span><span className="text-sm mono">{viewing.batch_lot}</span> · qty {viewing.quantity}</div>}
                </>
              )}
              <div><span className="text-xs uppercase text-slate-500 block">Description</span><p className="text-sm whitespace-pre-wrap">{viewing.description}</p></div>
              {viewing.photo_data && <img src={viewing.photo_data} alt="evidence" className="max-h-60 border border-slate-200 rounded-sm" />}
              {viewing.claim_amount > 0 && <div><span className="text-xs uppercase text-slate-500 block">Claim Amount</span><span className="text-sm mono font-semibold">${Number(viewing.claim_amount).toFixed(2)}</span></div>}
              {viewing.notes && <div><span className="text-xs uppercase text-slate-500 block">Notes</span><p className="text-sm whitespace-pre-wrap">{viewing.notes}</p></div>}
              {viewing.resolution && <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3"><span className="text-xs uppercase text-emerald-800 font-semibold block">Resolution</span><p className="text-sm text-emerald-900 whitespace-pre-wrap">{viewing.resolution}</p></div>}
              <RmaAttachments api={api} ticketId={viewing.id} />
              <LinkedPosCard api={api} param="warranty_ticket_id" id={viewing.id} testId="warranty-linked-pos" />
              <div className="text-xs text-slate-500 border-t border-slate-100 pt-3">
                Opened by {viewing.opened_by_name} on {new Date(viewing.opened_at).toLocaleString()}
                {viewing.resolved_at && <><br />Resolved by {viewing.resolved_by_name} on {new Date(viewing.resolved_at).toLocaleString()}</>}
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => printWarranty(viewing)} data-testid="warranty-print-btn">
                <Printer className="w-4 h-4 mr-1" /> Print
              </Button>
              {(validTransitions[viewing.status] || []).map(s => (
                <Button key={s} size="sm" variant={s === 'rejected' ? 'outline' : 'default'} onClick={() => setTransitionTo(s)} className={s === 'rejected' ? 'text-red-600 border-red-200' : s === 'approved' || s === 'completed' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'} data-testid={`warranty-transition-${s}`}>
                  → {s.replace('_', ' ')}
                </Button>
              ))}
              <Button size="sm" variant="outline" onClick={() => setViewing(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}

      {/* Transition dialog */}
      {transitionTo && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setTransitionTo(null)}>
          <div className="bg-white rounded-sm max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-200"><h3 className="font-semibold">Transition to {transitionTo.replace('_', ' ')}</h3></div>
            <div className="p-5 space-y-3">
              {(transitionTo === 'completed' || transitionTo === 'rejected') && (
                <div><Label>Resolution</Label><Textarea value={resolution} onChange={(e) => setResolution(e.target.value)} rows={3} className="rounded-sm" placeholder="Resolution details..." data-testid="warranty-resolution-input" /></div>
              )}
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setTransitionTo(null)}>Cancel</Button>
              <Button onClick={transition} className="bg-slate-900 hover:bg-slate-800" data-testid="warranty-confirm-transition">Confirm</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarrantyPage;
